import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import * as api from '../services/api';
import { useUser } from './UserContext';
import { showToast } from '../components/GlobalToast';

const NotificationContext = createContext();

// eslint-disable-next-line react-refresh/only-export-components
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user } = useUser();
  return <AccountNotificationProvider key={user?.id ?? 'guest'} user={user}>{children}</AccountNotificationProvider>;
};

const AccountNotificationProvider = ({ children, user }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  // Загружаем уведомления с бэкенда
  // silentLoad - если true, не показывает индикатор загрузки (для фонового обновления)
  const loadNotifications = useCallback(async (silentLoad = false) => {
    if (!user?.id) {
      setNotifications([]);
      return;
    }
    if (!silentLoad) {
      setLoading(true);
    }
    try {
      const data = await api.getNotifications();
      setNotifications(data);
    } catch (error) {
      console.error('Ошибка загрузки уведомлений:', error);
      let savedNotifications = null;
      try {
        const saved = localStorage.getItem(`polka_notifications_${user.id}`);
        savedNotifications = saved ? JSON.parse(saved) : null;
      } catch {
        console.warn('Некорректные polka_notifications в localStorage, использую fallback');
      }

      if (savedNotifications) {
        setNotifications(savedNotifications);
      } else {
        setNotifications([]);
      }
    } finally {
      if (!silentLoad) {
        setLoading(false);
      }
    }
  }, [user?.id]);

  // Загружаем уведомления при монтировании
  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Периодический опрос сервера для получения новых уведомлений (без индикатора загрузки)
  useEffect(() => {
    if (!user?.id) return undefined;

    const POLL_INTERVAL = 30000; // каждые 30 секунд
    
    const intervalId = setInterval(() => {
      loadNotifications(true); // silentLoad = true - без индикатора загрузки
    }, POLL_INTERVAL);

    return () => clearInterval(intervalId);
  }, [user?.id, loadNotifications]);

  // Сохраняем уведомления в localStorage
  useEffect(() => {
    if (notifications.length > 0) {
      if (user?.id) localStorage.setItem(`polka_notifications_${user.id}`, JSON.stringify(notifications));
    }
  }, [notifications, user?.id]);

  // Добавить уведомление
  const addNotification = (notification) => {
    const newNotification = {
      ...notification,
      id: Date.now(),
      time: new Date().toISOString(),
      read: false
    };
    setNotifications(prev => [newNotification, ...prev]);
    
    // Показываем тост для всех уведомлений
    if (!notification.silent) {
      showToast({
        type: notification.type === 'achievement' ? 'achievement' : 'info',
        title: notification.title || '',
        message: notification.message || '',
        duration: notification.duration || 4000
      });
    }
  };

  // Отметить как прочитанное
  const markAsRead = async (notificationId) => {
    try {
      await api.markNotificationAsRead(notificationId);
      setNotifications(prev =>
        prev.map(notification =>
          notification.id === notificationId
            ? { ...notification, read: true, unread_count: 0 }
            : notification
        )
      );
    } catch (error) {
      console.error('Ошибка при отметке уведомления как прочитанного:', error);
    }
  };

  // Отметить все как прочитанные
  const markAllAsRead = async () => {
    try {
      await api.markAllNotificationsAsRead();
      setNotifications(prev =>
        prev.map(notification => ({ ...notification, read: true, unread_count: 0 }))
      );
    } catch (error) {
      console.error('Ошибка при отметке всех уведомлений как прочитанных:', error);
    }
  };

  // Удалить уведомление
  const deleteNotification = (notificationId) => {
    setNotifications(prev =>
      prev.filter(notification => notification.id !== notificationId)
    );
  };

  // Очистить все уведомления
  const clearAllNotifications = () => {
    setNotifications([]);
  };

  // Получить количество непрочитанных
  const getUnreadCount = () => {
    return notifications.reduce((sum,n) => sum + Number(n.unread_count ?? (!n.read ? 1 : 0)),0);
  };

  // Получить отформатированное время
  const getFormattedTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'только что';
    if (minutes < 60) return `${minutes} мин. назад`;
    if (hours < 24) return `${hours} ч. назад`;
    if (days < 7) return `${days} дн. назад`;
    return date.toLocaleDateString('ru-RU');
  };

  return (
    <NotificationContext.Provider value={{
      notifications,
      loading,
      addNotification,
      markAsRead,
      markAllAsRead,
      deleteNotification,
      clearAllNotifications,
      getUnreadCount,
      getFormattedTime,
      loadNotifications,
      muteThread: async (id, muted) => { await api.muteNotificationThread(id, muted); await loadNotifications(true); }
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;